import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ASSETS } from "../lib/assets";
import { Menu, X, ChevronDown } from "lucide-react";

const PROYECTOS_SUBLINKS = [
  { to: "/proyectos/enclaves", label: "Enclaves" },
  { to: "/proyectos/residencias", label: "Residencias" },
];

const LINKS = [
  { to: "/proyectos", label: "Proyectos", hasMenu: true },
  { to: "/baru", label: "Barú" },
  { to: "/cuaderno", label: "Cuaderno" },
  { to: "/contacto", label: "Contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoverProy, setHoverProy] = useState(false);
  const proyTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setHoverProy(false);
  }, [location.pathname]);

  const openProy = () => {
    if (proyTimer.current) clearTimeout(proyTimer.current);
    setHoverProy(true);
  };
  const scheduleCloseProy = () => {
    if (proyTimer.current) clearTimeout(proyTimer.current);
    proyTimer.current = setTimeout(() => setHoverProy(false), 180);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[rgba(7,7,7,0.55)] backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="pc-container flex items-center justify-between py-6 md:py-5">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="inline-flex items-center"
          aria-label="Inicio Paco Cabrera"
        >
          <img
            data-testid="navbar-logo"
            src={ASSETS.logoWhite}
            alt="Paco Cabrera · Arquitectura y Permanencia"
            className="h-20 md:h-28 lg:h-32 w-auto select-none"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-12 xl:gap-16">
          {LINKS.map((l) => {
            const isProyectos = l.to === "/proyectos";
            return (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={isProyectos ? openProy : undefined}
                onMouseLeave={isProyectos ? scheduleCloseProy : undefined}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.to.replace("/", "")}`}
                  className={({ isActive }) =>
                    `nav-link inline-flex items-center gap-1 ${isActive ? "is-active" : ""}`
                  }
                >
                  <span>{l.label}</span>
                  {l.hasMenu && (
                    <ChevronDown
                      size={12}
                      strokeWidth={1.2}
                      className={`opacity-70 transition-transform duration-300 ${
                        hoverProy && isProyectos ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </NavLink>

                {/* Dropdown Proyectos */}
                {isProyectos && (
                  <div
                    data-testid="proyectos-dropdown"
                    onMouseEnter={openProy}
                    onMouseLeave={scheduleCloseProy}
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-5 transition-all duration-300 ${
                      hoverProy
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                  >
                    <div className="bg-[rgba(7,7,7,0.92)] backdrop-blur-md border border-[var(--pc-line)] py-5 px-7 flex flex-col gap-4 min-w-[180px]">
                      {PROYECTOS_SUBLINKS.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          data-testid={`proyectos-sublink-${s.label.toLowerCase()}`}
                          className="nav-link whitespace-nowrap"
                          style={{ fontSize: 12, letterSpacing: "0.28em" }}
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <button
          data-testid="navbar-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
          className={`${
            open ? "hidden" : "inline-flex"
          } items-center text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] transition-colors p-3 -mr-2 z-[60] relative`}
        >
          <Menu size={26} strokeWidth={1.2} />
        </button>
      </div>

      {/* Fullscreen overlay — rendered into document.body so it escapes any
          backdrop-filter containing block created by the scrolled header. */}
      {createPortal(
        <div
          data-testid="navbar-overlay"
          className={`fixed inset-0 transition-opacity duration-500 ${
            open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
          style={{ top: 0, backgroundColor: "#070707", zIndex: 1000 }}
        >
          {/* Persistent close button (top-right, always visible while overlay is open) */}
          <button
            data-testid="overlay-close-button"
            onClick={() => setOpen(false)}
            aria-label="Cerrar menú"
            className={`absolute top-5 md:top-6 right-6 md:right-12 z-[70] inline-flex items-center gap-3 text-[var(--pc-text)] hover:text-[var(--pc-bronze-bright)] transition-colors ${
              open ? "" : "pointer-events-none"
            }`}
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 300,
              fontSize: 12,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
            }}
          >
            <span>Cerrar</span>
            <X size={22} strokeWidth={1.2} />
          </button>

          <div className="pc-container h-full flex flex-col justify-center">
            <div className="flex flex-col gap-10">
              {LINKS.map((l, idx) => (
                <div key={l.to} style={{ animation: open ? `pcFade .9s ${idx * 0.08 + 0.1}s both` : "none" }}>
                  <NavLink
                    to={l.to}
                    data-testid={`overlay-link-${l.to.replace("/", "")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `headline-lg text-3xl md:text-5xl transition-colors ${
                        isActive ? "text-[var(--pc-bronze-bright)]" : ""
                      } hover:text-[var(--pc-bronze-bright)]`
                    }
                  >
                    {l.label}
                  </NavLink>
                  {l.hasMenu && (
                    <div className="mt-5 ml-1 flex flex-col gap-3">
                      {PROYECTOS_SUBLINKS.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          data-testid={`overlay-sublink-${s.label.toLowerCase()}`}
                          onClick={() => setOpen(false)}
                          className="label-small hover:text-[var(--pc-bronze-bright)] transition-colors"
                        >
                          — {s.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-20 label-small">Cartagena · Barú · Colombia</div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
